// Client Portal - Dashboard API Service
// Handles dashboard and analytics API calls for client portal users

import axios from 'axios';
import { store } from '../../../store';
import { getToken, getRefreshToken, setToken } from '../../../shared/utils/tokenStorage';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Create axios instance
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = getRefreshToken();
        const response = await axios.post(`${API_BASE_URL}/api/token/refresh/`, {
          refresh: refreshToken,
        });
        setToken(response.data.access);
        originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Dashboard API Service for Client Portal
class DashboardAPIService {
  // ============= Dashboard Overview =============

  // Get dashboard summary
  async getDashboardSummary() {
    try {
      const response = await apiClient.get('/client/dashboard/summary/');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      return {
        success: false,
        error: error.message,
        data: {
          fleet_status: {},
          recent_events: [],
          active_alerts: [],
          key_metrics: {},
          quick_stats: {},
        },
      };
    }
  }

  // Get key performance indicators
  async getKPIs(params = {}) {
    try {
      const response = await apiClient.get('/client/dashboard/kpis/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      return {
        success: false,
        error: error.message,
        data: {
          fleet_utilization: 0,
          fuel_efficiency: 0,
          safety_score: 0,
          maintenance_compliance: 0,
          cost_per_km: 0,
          on_time_delivery: 0,
        },
      };
    }
  }

  // Get recent activities
  async getRecentActivities(limit = 20) {
    try {
      const response = await apiClient.get('/client/dashboard/activities/', {
        params: { limit },
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // ============= Analytics Charts =============

  // Get fleet performance trends
  async getFleetPerformanceTrends(params = {}) {
    try {
      // For now, return empty data as this endpoint may not exist
      return {
        success: true,
        data: [],
      };
    } catch (error) {
      console.error('Error fetching fleet performance:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get driver risk scores
  async getDriverRiskScores(params = {}) {
    try {
      const response = await apiClient.get('/client/analytics/driver-risk-scores/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching driver risk scores:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get vehicle risk scores
  async getVehicleRiskScores(params = {}) {
    try {
      const response = await apiClient.get('/client/analytics/vehicle-risk-scores/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching vehicle risk scores:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get event heatmap data
  async getEventHeatmap(params = {}) {
    try {
      const response = await apiClient.get('/client/analytics/event-heatmap/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching event heatmap:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get monthly events chart data
  async getMonthlyEvents(params = {}) {
    try {
      const response = await apiClient.get('/client/analytics/monthly-events/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching monthly events:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get speeding trends
  async getSpeedingTrends(params = {}) {
    try {
      const response = await apiClient.get('/client/analytics/speeding-trends/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching speeding trends:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get risk profile distribution
  async getRiskProfile(params = {}) {
    try {
      const response = await apiClient.get('/client/analytics/risk-profile/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching risk profile:', error);
      return {
        success: false,
        error: error.message,
        data: {
          low_risk: 0,
          medium_risk: 0,
          high_risk: 0,
          critical_risk: 0,
        },
      };
    }
  }

  // ============= Top Lists =============

  // Get top drivers
  async getTopDrivers(params = {}) {
    try {
      const response = await apiClient.get('/client/analytics/top-drivers/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching top drivers:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get top vehicles
  async getTopVehicles(params = {}) {
    try {
      const response = await apiClient.get('/client/analytics/top-vehicles/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching top vehicles:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // ============= Alerts & Notifications =============

  // Get active alerts
  async getActiveAlerts() {
    try {
      const response = await apiClient.get('/client/alerts/active/');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching active alerts:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Get alert history
  async getAlertHistory(params = {}) {
    try {
      const response = await apiClient.get('/client/alerts/history/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching alert history:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Acknowledge alert
  async acknowledgeAlert(alertId) {
    try {
      const response = await apiClient.post(`/client/alerts/${alertId}/acknowledge/`, {
        acknowledged_by: store.getState().auth?.user?.id,
        acknowledged_at: new Date().toISOString(),
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // ============= Dashboard Analytics =============

  // Get driver statistics
  async getDriverStatistics(params = {}) {
    try {
      const response = await apiClient.get('/client/analytics/drivers/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching driver statistics:', error);
      return {
        success: false,
        error: error.message,
        data: {
          totalDrivers: 0,
          activeDrivers: 0,
          topPerformers: [],
          riskDrivers: [],
          averageScore: 0,
        },
      };
    }
  }

  // Get event statistics
  async getEventStatistics(params = {}) {
    try {
      const response = await apiClient.get('/client/analytics/events/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching event statistics:', error);
      return {
        success: false,
        error: error.message,
        data: {
          totalEvents: 0,
          harshBraking: 0,
          rapidAcceleration: 0,
          speeding: 0,
          idling: 0,
          geofenceViolations: 0,
        },
      };
    }
  }

  // Get analytics data
  async getAnalytics(params = {}) {
    try {
      // For now, return empty data as this endpoint may not exist
      return {
        success: true,
        data: {
          fuelTrends: [],
          speedingTrends: [],
          utilizationRate: [],
          costAnalysis: [],
          riskMatrix: [],
        },
      };
    } catch (error) {
      console.error('Error fetching analytics:', error);
      return {
        success: false,
        error: error.message,
        data: {
          fuelTrends: [],
          speedingTrends: [],
          utilizationRate: [],
          costAnalysis: [],
          riskMatrix: [],
        },
      };
    }
  }

  // Get recent events
  async getRecentEvents(params = {}) {
    try {
      const response = await apiClient.get('/client/events/recent/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching recent events:', error);
      return {
        success: false,
        error: error.message,
        data: {
          results: [],
          total_count: 0,
        },
      };
    }
  }

  // Export report
  async exportReport(params = {}) {
    try {
      const response = await apiClient.post('/client/reports/export/', params, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting report:', error);
      throw error;
    }
  }

  // ============= Billing & Usage =============

  // Get billing summary
  async getBillingSummary() {
    try {
      const response = await apiClient.get('/client/billing/summary/');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching billing summary:', error);
      return {
        success: false,
        error: error.message,
        data: {
          current_balance: 0,
          last_payment: null,
          next_billing_date: null,
          plan_type: '',
          usage_summary: {},
        },
      };
    }
  }

  // Get usage statistics
  async getUsageStats(params = {}) {
    try {
      const response = await apiClient.get('/client/billing/usage/', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching usage stats:', error);
      return {
        success: false,
        error: error.message,
        data: {},
      };
    }
  }

  // ============= Custom Widgets =============

  // Get widget data
  async getWidgetData(widgetType, params = {}) {
    try {
      const response = await apiClient.get(`/client/widgets/${widgetType}/`, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error(`Error fetching ${widgetType} widget data:`, error);
      return {
        success: false,
        error: error.message,
        data: null,
      };
    }
  }

  // Get dashboard layout
  async getDashboardLayout() {
    try {
      const response = await apiClient.get('/client/dashboard/layout/');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching dashboard layout:', error);
      return {
        success: false,
        error: error.message,
        data: null,
      };
    }
  }

  // Save dashboard layout
  async saveDashboardLayout(layout) {
    try {
      const response = await apiClient.post('/client/dashboard/layout/', {
        layout,
        updated_by: store.getState().auth?.user?.id,
        updated_at: new Date().toISOString(),
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error saving dashboard layout:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // ============= Quick Actions =============

  // Get quick actions
  async getQuickActions() {
    try {
      const response = await apiClient.get('/client/dashboard/quick-actions/');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching quick actions:', error);
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }
  }

  // Execute quick action
  async executeQuickAction(actionId, params = {}) {
    try {
      const response = await apiClient.post(
        `/client/dashboard/quick-actions/${actionId}/execute/`,
        {
          ...params,
          executed_by: store.getState().auth?.user?.id,
          executed_at: new Date().toISOString(),
        }
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error executing quick action:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }
}

// Export singleton instance
const dashboardAPI = new DashboardAPIService();

export default dashboardAPI;
export { DashboardAPIService };
